import {
  ReportFactory,
  serializedTypes,
  commonTypes,
} from '@ltvco/refresh-lib/v1';
import { PersonSearchParamsv2, RelatedReport } from '@ltvco/refresh-lib/v2';

import { SEARCH_TYPES } from 'components/DeepSearch/constants';
import { PermalinkOrTeaserOrParamsResult } from 'components/DeepSearch/types/conversationTypes';
import {
  getPersonPermalink,
  getPhoneNumberPermalink,
  getEmailPermalink,
  getPropertyPermalink,
  getVehiclePermalink,
  getUsernamePermalink,
  getAncestorPermalink,
} from 'components/DeepSearch/utils/permalinkUtils';

export interface TransformedReportData {
  username?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  vin?: string;
  licensePlate?: string;
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  bvid?: string;
  permalink?: string;
  zipcode?: string;
  family_search_id?: string;
}

export interface RelatedReportTransformResult {
  reportType: string;
  data?: TransformedReportData;
  personSearch?: PersonSearchParamsv2;
  useBvid?: boolean;
}

export interface ReportInfoForRelatedReports {
  personName: string;
  reportType: string;
  potentialOwners: any[];
  relatives: serializedTypes.Connection[];
  addresses: commonTypes.Addresses;
  phones: commonTypes.Phones;
  emails: commonTypes.Emails;
  usernames: commonTypes.Usernames;
}

type ReportDataForExtraction = {
  entities?: {
    people?: any[];
    properties?: Array<{
      parcel_address?: { full?: string };
      address?: string;
      owners?: any[];
    }>;
  };
  data?: {
    entities?: { people?: any[] };
  };
  address?: string;
};

/**
 * Extracts report info for RelatedReports from report data.
 * Handles special structures for property, ancestor, and standard person-based reports.
 */
export function getReportInfoFromReportData(
  reportType: string,
  reportData: ReportDataForExtraction | undefined
): ReportInfoForRelatedReports {
  if (!reportData) {
    return {
      personName: 'Unknown',
      reportType: reportType ?? 'unknown',
      potentialOwners: [],
      relatives: [],
      addresses: [],
      phones: [],
      emails: [],
      usernames: [],
    };
  }

  // Property reports: entities.properties, owners
  if (reportType === SEARCH_TYPES.PROPERTY) {
    const property = reportData?.entities?.properties?.[0];
    const owners = property?.owners ?? [];
    const personName =
      property?.parcel_address?.full ||
      property?.address ||
      (reportData as { address?: string })?.address ||
      'Unknown';

    return {
      personName,
      reportType,
      potentialOwners: owners,
      relatives: [],
      addresses: (owners as any[]).flatMap((o) => o?.contact?.addresses ?? []),
      phones: (owners as any[]).flatMap((o) => o?.contact?.phones ?? []),
      emails: (owners as any[]).flatMap((o) => o?.contact?.emails ?? []),
      usernames: (owners as any[]).flatMap((o) => o?.social?.usernames ?? []),
    };
  }

  // Genealogy/ancestor reports: use ReportFactory (same as AncestorChatBvReport and
  // processAncestorSearchReportForOverviewAndOwners) - API can return entities at top
  // level or nested inside data
  if (reportType === SEARCH_TYPES.ANCESTOR) {
    const coreResult = reportData as any;
    const report = coreResult?.entities
      ? ReportFactory.create(coreResult, 'person')
      : ReportFactory.create(coreResult?.data, 'person');

    const person = report?.data?.people?.[0];
    if (!person) {
      return {
        personName: 'Unknown',
        reportType,
        potentialOwners: [],
        relatives: [],
        addresses: [],
        phones: [],
        emails: [],
        usernames: [],
      };
    }

    const people = report?.data?.people ?? [];
    const relatives =
      person?.connections?.relatives ??
      (person as any)?.rawData?.connections?.relatives ??
      [];

    // Genealogy: main person + relatives have contact.addresses (Birth/Death/Burial)
    const mainAddresses = person?.contact?.addresses ?? [];
    const relativeAddresses = (relatives as any[]).flatMap(
      (r) => r?.contact?.addresses ?? []
    );
    const addresses = [...mainAddresses, ...relativeAddresses];

    return {
      personName: person?.identity?.names?.[0]?.full ?? 'Unknown',
      reportType,
      potentialOwners: people,
      relatives,
      addresses,
      phones: person?.contact?.phones ?? [],
      emails: person?.contact?.emails ?? [],
      usernames: (person as any)?.social?.usernames ?? [],
    };
  }

  // Person-based reports (person, phone, email, etc.)
  const people = reportData?.entities?.people ?? [];
  const mainPerson = people[0];

  return {
    personName: mainPerson?.identity?.names?.[0]?.full ?? 'Unknown',
    reportType,
    potentialOwners: people,
    relatives: mainPerson?.connections?.relatives ?? [],
    addresses: mainPerson?.contact?.addresses ?? [],
    phones: mainPerson?.contact?.phones ?? [],
    emails: mainPerson?.contact?.emails ?? [],
    usernames: mainPerson?.social?.usernames ?? [],
  };
}

const buildAddress = (parsedData: any) => {
  const parts = [
    parsedData?.primary_address_number,
    parsedData?.pre_direction,
    parsedData?.street_name,
    parsedData?.street_type,
    parsedData?.post_direction,
    parsedData?.unit_number,
  ]
    .filter(Boolean)
    .join(' ');

  return parts || '';
};

export const getRelatedReportData = (
  relatedReport: RelatedReport
): RelatedReportTransformResult => {
  const { data, reportType } = relatedReport;
  const bvid = data?.bvId;

  switch (reportType) {
    case SEARCH_TYPES.PERSON: {
      // Genealogy relatives have fs_ids (FamilySearch) but no ids (BVID)
      const fsId = (data?.identity as { fs_ids?: string[] })?.fs_ids?.[0];
      const hasRealBvid =
        data?.identity?.ids?.length && (bvid || data?.identity?.ids?.[0]);

      if (fsId && !hasRealBvid) {
        return {
          reportType: SEARCH_TYPES.ANCESTOR,
          data: { family_search_id: fsId },
        };
      }
      if (bvid) {
        return {
          reportType,
          data: { bvid },
          useBvid: true,
        };
      }
      const personSearch: PersonSearchParamsv2 = {
        fName:
          data?.identity?.names?.[0]?.parsed?.first ||
          data?.query_params?.fname ||
          '',
        mName:
          data?.identity?.names?.[0]?.parsed?.middle ||
          data?.query_params?.mn ||
          '',
        lName:
          data?.identity?.names?.[0]?.parsed?.last ||
          data?.query_params?.ln ||
          '',
        age: data?.age || data?.query_params?.age || '',
        city:
          data?.contact?.addresses?.[0]?.parsed?.city ||
          data?.query_params?.city ||
          '',
        state:
          data?.contact?.addresses?.[0]?.parsed?.state ||
          data?.query_params?.state ||
          'All',
      };
      return {
        reportType: 'teaser',
        personSearch,
      };
    }
    case SEARCH_TYPES.PHONE: {
      const phoneData: TransformedReportData = {
        phoneNumber: data?.number,
      };
      return {
        reportType,
        data: phoneData,
      };
    }
    case SEARCH_TYPES.EMAIL: {
      const emailData: TransformedReportData = {
        email: data?.email,
      };
      return {
        reportType,
        data: emailData,
      };
    }
    case SEARCH_TYPES.PROPERTY: {
      // Address from addressRelatedReport: has full, parsed (may be null for genealogy)
      const streetAddress = data?.parsed ? buildAddress(data?.parsed) : null;
      const fullAddress = (data as { full?: string })?.full;
      const address = streetAddress || fullAddress;

      const propertyData: TransformedReportData = {
        address: address || undefined,
        city: data?.parsed?.city,
        state: data?.parsed?.state,
        zipcode: data?.parsed?.zip5,
      };
      return {
        reportType,
        data: propertyData,
      };
    }
    case SEARCH_TYPES.VEHICLE: {
      const vehicleData: TransformedReportData = {
        vin: data?.vin,
        licensePlate: data?.license_plate,
        state: data?.state,
        year: data?.year,
        make: data?.make,
        model: data?.model,
        trim: data?.trim,
      };
      return {
        reportType,
        data: vehicleData,
      };
    }
    case SEARCH_TYPES.OBITUARY: {
      const obituaryData = {
        permalink: data?.permalink,
        firstName: data?.identity?.names?.[0]?.parsed?.first,
        lastName: data?.identity?.names?.[0]?.parsed?.last,
        birthYear: data?.identity?.dobs?.[0]?.date?.parsed?.year,
        deathYear: data?.identity?.dods?.[0]?.date?.parsed?.year,
        city: data?.contact?.addresses?.[0]?.parsed?.city,
        state: data?.contact?.addresses?.[0]?.parsed?.state,
      };

      if (obituaryData.permalink) {
        return {
          reportType,
          data: { permalink: obituaryData.permalink },
        };
      } else if (obituaryData.firstName && obituaryData.lastName) {
        const personSearch: PersonSearchParamsv2 = {
          fName: obituaryData.firstName,
          lName: obituaryData.lastName,
          city: obituaryData.city,
          state: obituaryData.state || 'All',
          age: '',
          birthYear: obituaryData.birthYear,
          deathYear: obituaryData.deathYear,
        };
        return {
          reportType: 'teaser',
          personSearch,
        };
      }
      return {
        reportType,
        data: {},
      };
    }
    case SEARCH_TYPES.USERNAME: {
      const usernameData: TransformedReportData = {
        username: data?.username,
      };
      return {
        reportType,
        data: usernameData,
      };
    }
    default:
      return {
        reportType,
        data: {},
      };
  }
};

export async function getRelatedReportPermalink(
  reportType: string,
  data: TransformedReportData
): Promise<PermalinkOrTeaserOrParamsResult> {
  switch (reportType) {
    case SEARCH_TYPES.PERSON:
      if (data.bvid) {
        return await getPersonPermalink(data.bvid);
      }
      return { error: 'BVID required for person reports' };

    case SEARCH_TYPES.OBITUARY:
      if (data.permalink) {
        return { permalink: data.permalink };
      }
      return { error: 'Permalink required for obituary reports' };

    case SEARCH_TYPES.ANCESTOR:
      if (data.family_search_id) {
        return await getAncestorPermalink(data.family_search_id);
      }
      return { error: 'FamilySearch ID required for ancestor reports' };

    case SEARCH_TYPES.PHONE:
      if (data.phoneNumber) {
        return await getPhoneNumberPermalink(data.phoneNumber);
      }
      return { error: 'Phone number required for phone reports' };

    case SEARCH_TYPES.EMAIL:
      if (data.email) {
        return await getEmailPermalink(data.email);
      }
      return { error: 'Email required for email reports' };

    case SEARCH_TYPES.PROPERTY:
      if (data.address) {
        return await getPropertyPermalink(
          data.address,
          data.city,
          data.state,
          data.zipcode
        );
      }
      return { error: 'Address required for property reports' };

    case SEARCH_TYPES.VEHICLE:
      if (data.vin) {
        return await getVehiclePermalink(data);
      }
      if (data.licensePlate && data.state) {
        return await getVehiclePermalink({
          licensePlate: data.licensePlate,
          state: data.state,
        });
      }
      if (data.year && data.make && data.model && data.trim) {
        return await getVehiclePermalink({
          year: data.year,
          make: data.make,
          model: data.model,
          trim: data.trim,
        });
      }
      return { error: 'Invalid vehicle search parameters' };

    case SEARCH_TYPES.USERNAME:
      if (data.username) {
        return await getUsernamePermalink(data.username);
      }
      return { error: 'Username required for username reports' };

    default:
      return { error: `Unsupported report type: ${reportType}` };
  }
}
